import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { useKontakteHandling } from "../../hooks/kontakte/useKontakteHandling";
import { useImportKontakte } from "../../hooks/kontakte/ImportKontakte";
import { useGetAufgabe } from "../../client/hooks";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useQueryClient } from "react-query";
import {VisuallyHiddenInput} from "../../styles/kontakte/kontakte.syles.upload";
import {kontakteStyles} from "../../styles/kontakte/kontakte.styles";

type Props = {
  aufgabeId: string;
};

export default function ImportContactsButton({ aufgabeId }: Props) {
  const { translation } = useKontakteHandling();
  const [open, setOpen] = useState(false);
  const [confirmLiveOpen, setConfirmLiveOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [aufgabeStatus, setAufgabeStatus] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const queryClient = useQueryClient();

  const { templateDownload, importCsv, isUploading, report, error, validateFile, validationError, reset } = useImportKontakte(aufgabeId);
  const getAufgabe = useGetAufgabe();

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const aufgabe = await getAufgabe(aufgabeId);
        setAufgabeStatus(aufgabe.status as any);
      } catch (_e) {
        setAufgabeStatus(null);
      }
    })();
  }, [open, aufgabeId, getAufgabe]);

  const handleOpen = () => {
    reset();
    setSelectedFile(null);
    setShowResult(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmLiveOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) validateFile(file);
  };

  const proceedImport = async () => {
    if (!selectedFile) return;
    try {
      await importCsv(selectedFile);
      await Promise.all([
        queryClient.invalidateQueries(["kontakte-view"]),
      ]);
      setShowResult(true);
    } catch (_e) {
      setShowResult(true);
    } finally {
      setConfirmLiveOpen(false);
    }
  };

  const onSubmit = async () => {
    if (!selectedFile) return;
    if (!validateFile(selectedFile)) return;
    if (aufgabeStatus === "LIVE") {
      setConfirmLiveOpen(true);
      return;
    }
    await proceedImport();
  };

  const validationMessage = useMemo(() => {
    switch (validationError) {
      case "no-file-selected":
        return translation("no-file-selected");
      case "invalid-file-type":
        return translation("invalid-file-type");
      case "file-too-large":
        return translation("file-too-large");
      default:
        return null;
    }
  }, [validationError, translation]);



  return (
    <>
      <Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleOpen}>
        {translation("import-csv")}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{translation("import-csv")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void templateDownload()}>
              {translation("download-template")}
            </Button>
            <div>
                <Stack direction={"row"} sx={{alignItems:"center"}} gap={1}>
                    <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        startIcon={<CloudUploadIcon />}
                    >
                        {translation("file-choose")}
                        <VisuallyHiddenInput
                            id="kontakt-import-file"
                            type="file"
                            onChange={handleFileChange}
                            accept=".csv,text/csv"
                        />
                    </Button>
                    <Typography>{selectedFile?.name}</Typography>
                </Stack>

              {validationMessage && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {validationMessage}
                </Typography>
              )}
            </div>
            {showResult && (
              <Stack spacing={1}>
                <Typography variant="subtitle1">{translation("import-summary")}</Typography>
                {report && (
                  <>
                    <Typography variant="body2">
                      {translation("import-success", { imported: report.imported, total: report.total })}
                    </Typography>
                    {report.errors?.length > 0 && (
                      <div>
                        <Typography variant="body2" sx={kontakteStyles.descriptionErrorBody}>
                          {translation("import-errors")} ({report.errors.length})
                        </Typography>
                        <ul style={kontakteStyles.errorListItemStyle}>
                          {report.errors.slice(0, 10).map((e, idx) => (
                            <li key={`${e.line}-${idx}`}>
                              <Typography variant="body2">
                                {translation("line")}: {e.line} – {e.reason}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                        {report.errors.length > 10 && (
                          <Typography variant="caption" color="text.secondary">
                            +{report.errors.length - 10} {translation("more")}
                          </Typography>
                        )}
                      </div>
                    )}
                  </>
                )}
                {!report && error && (
                  <Typography variant="body2" color="error">
                    {translation("import-failed")}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{translation("close")}</Button>
          <Button onClick={onSubmit} disabled={!selectedFile || isUploading} variant="contained">
            {isUploading ? translation("uploading") : translation("upload")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmLiveOpen} onClose={() => setConfirmLiveOpen(false)}>
        <DialogTitle>{translation("live-warning-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translation("live-warning-text")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLiveOpen(false)}>{translation("cancel")}</Button>
          <Button onClick={() => void proceedImport()} variant="contained" color="warning">
            {translation("continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
